import { useState } from 'react'
import {
  acceptAiRunbook,
  addAiRunbook,
  dismissAiRunbook,
  saveAiRunbookSafe,
  type AiAgentShell,
  type AiRunbookSuggestion,
  type AiVettedRow,
} from '../../api/settingsAiAgent'
import { failed, NoteText, type Note } from './parts'

/**
 * Fila "Runbooks": ce a reparat agentul de cel putin doua ori, si catalogul
 * vetted in care intra.
 *
 * Propunerile NU se stocheaza - se calculeaza de fiecare data din memorie, deci
 * o propunere respinsa nu se intoarce, iar una noua apare singura.
 *
 * Bifa "incident-safe" spune ce are voie sa plece pe loc, fara semnatura -
 * din playbook-ul Autonomy sau din `remediate` al agentului. Implicit nimic, si
 * fiecare bifa intra in jurnal, cu numele omului.
 */
export function RunbooksTab({ page, onReload }: { page: AiAgentShell; onReload(): void }) {
  const isCompany = page.scope === 'company'

  return (
    <>
      <div className="panel panel-default">
        <div className="panel-heading">
          <strong>Suggested runbooks</strong>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
            fixes the agent remembered at least twice{isCompany ? ' on ' + page.companyName : ', on any company'} -
            accept them into the vetted catalog
            {isCompany ? ' of this company' : ' of the platform (inherited by every company)'}, or dismiss
          </span>
        </div>
        <div className="panel-body">
          {!page.runbooksReady ? (
            <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
              Needs the memory table and the <code>2026_09_03_100002</code> migration.
            </p>
          ) : page.runbooks.length === 0 ? (
            <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
              Nothing to propose yet: no fix has been remembered twice.
            </p>
          ) : (
            <table className="table" style={{ fontSize: 13, margin: 0 }}>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>What worked</th>
                  <th>Seen</th>
                  <th style={{ width: '44%' }}>Runbook</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {page.runbooks.map((rb) => (
                  <SuggestionRow key={rb.fingerprint} page={page} rb={rb} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="panel panel-default">
        <div className="panel-heading">
          <strong>
            Vetted catalog
            {isCompany ? ": inherited from Global, plus this company's own" : ' of the platform'}
          </strong>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
            tick a runbook to let the Autonomy playbook and the agent in Incident mode run it without a signature.
            Placeholders like <code>{'{{service}}'}</code> are filled from the alarm.
            {isCompany
              ? ' Inherited runbooks are read-only here: what Global defines comes down; what the company defines stays with the company.'
              : ' What is defined here is inherited by every company.'}
          </span>
        </div>
        <div className="panel-body">
          {!page.vettedSafeReady ? (
            <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
              Needs the <code>2026_09_04_100004_standard_commands_incident_safe</code> migration.
            </p>
          ) : (
            <>
              <NewRunbook page={page} onAdded={onReload} />

              {page.vetted.length === 0 ? (
                <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
                  The vetted catalog is empty. Add a runbook above, or accept a suggested one.
                </p>
              ) : (
                <table className="table table-condensed" style={{ fontSize: 12, margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Safe</th>
                      <th style={{ width: 70 }} title="On an email request with the 'anything about the sender' setting, the runbook may run on any company machine - only with the sender's own account as {{user}} / {{username}}.">Person</th>
                      <th style={{ width: 260 }}>Runbook</th>
                      <th>Command</th>
                      <th style={{ width: 150 }}>Type / category</th>
                      <th style={{ width: 110 }}>Scope</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.vetted.map((row) => (
                      <VettedRow key={row.ID} page={page} row={row} />
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

function SuggestionRow({ page, rb }: { page: AiAgentShell; rb: AiRunbookSuggestion }) {
  const [title, setTitle] = useState(rb.text.substring(0, 120))
  const [command, setCommand] = useState(rb.commands[0] ?? '')
  const [safe, setSafe] = useState(false)
  const [category, setCategory] = useState(0)
  const [type, setType] = useState(0)
  const [note, setNote] = useState<Note | null>(null)
  const [done, setDone] = useState(false)

  const fields = {
    company_id: rb.company_id,
    fingerprint: rb.fingerprint,
    title,
    command,
    category_id: category,
    type,
    incident_safe: safe ? 1 : 0,
    scope: page.scope,
  }

  const accept = () => {
    setNote({ text: '…' })
    void acceptAiRunbook(fields)
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setNote({ text: `In the catalog (#${r.catalog_id})` })
        setDone(true)
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const dismiss = () => {
    setNote({ text: '…' })
    void dismissAiRunbook(fields)
      .then((r) => {
        if (!r.ok) {
          setNote({ text: 'Failed', bad: true })
          return
        }
        setNote({ text: 'Dismissed' })
        setDone(true)
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <tr>
      <td>{rb.company || '#' + rb.company_id}</td>
      <td>
        {rb.text}
        <div className="text-muted" style={{ fontSize: 11 }}>
          last {String(rb.last_seen).substring(0, 16)}
        </div>
      </td>
      <td>
        {rb.seen}× on {rb.devices.slice(0, 4).join(', ')}
        {rb.devices.length > 4 ? '...' : ''}
      </td>
      <td>
        <input
          type="text"
          className="form-control input-sm"
          placeholder="Title in the catalog"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          className="form-control input-sm"
          rows={2}
          style={{ marginTop: 4, fontFamily: 'monospace' }}
          placeholder="The command (from the conversations where it worked)"
          value={command}
          onChange={(e) => setCommand(e.target.value)}
        />
        {rb.commands.length > 1 ? (
          <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
            also ran: {rb.commands.slice(1).join(' | ')}
          </div>
        ) : null}
        <label style={{ fontWeight: 400, fontSize: 11, margin: '4px 0 0' }}>
          <input type="checkbox" checked={safe} onChange={(e) => setSafe(e.target.checked)} /> allowed to run on the
          spot (incident-safe)
        </label>
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <select
            className="form-control input-sm"
            value={category}
            onChange={(e) => setCategory(Number(e.target.value))}
          >
            <option value={0}>category...</option>
            {page.cmdCategories.map((c) => (
              <option key={c.ID} value={c.ID}>
                {c.name}
              </option>
            ))}
          </select>
          <select className="form-control input-sm" value={type} onChange={(e) => setType(Number(e.target.value))}>
            <option value={0}>type...</option>
            {page.cmdTypes.map((c) => (
              <option key={c.ID} value={c.ID}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button type="button" className="btn btn-primary btn-sm" disabled={done} onClick={accept}>
          Add to catalog
        </button>{' '}
        <button type="button" className="btn btn-default btn-sm" disabled={done} onClick={dismiss}>
          Dismiss
        </button>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          <NoteText note={note} />
        </div>
      </td>
    </tr>
  )
}

function NewRunbook({ page, onAdded }: { page: AiAgentShell; onAdded(): void }) {
  const [title, setTitle] = useState('')
  const [command, setCommand] = useState('')
  const [category, setCategory] = useState(0)
  const [type, setType] = useState(0)
  const [safe, setSafe] = useState(false)
  const [note, setNote] = useState<Note | null>(null)

  const add = () => {
    setNote({ text: '…' })
    void addAiRunbook({
      title,
      command,
      category_id: category,
      type,
      incident_safe: safe ? 1 : 0,
      scope: page.scope,
    })
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setNote({ text: `Added (#${r.id})` })
        setTitle('')
        setCommand('')
        onAdded()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
      <div style={{ flex: '1 1 200px' }}>
        <label style={{ fontSize: 12, margin: 0 }}>New runbook - title</label>
        <input
          type="text"
          className="form-control input-sm"
          placeholder="Restart a Windows service by name"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div style={{ flex: '2 1 320px' }}>
        <label style={{ fontSize: 12, margin: 0 }}>Command (placeholders: {'{{service}}'}, {'{{mount}}'})</label>
        <input
          type="text"
          className="form-control input-sm"
          style={{ fontFamily: 'monospace' }}
          placeholder="Restart-Service -Name '{{service}}'"
          value={command}
          onChange={(e) => setCommand(e.target.value)}
        />
      </div>
      <div>
        <label style={{ fontSize: 12, margin: 0 }}>Category</label>
        <select className="form-control input-sm" value={category} onChange={(e) => setCategory(Number(e.target.value))}>
          <option value={0}>-</option>
          {page.cmdCategories.map((c) => (
            <option key={c.ID} value={c.ID}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label style={{ fontSize: 12, margin: 0 }}>Type</label>
        <select className="form-control input-sm" value={type} onChange={(e) => setType(Number(e.target.value))}>
          <option value={0}>-</option>
          {page.cmdTypes.map((c) => (
            <option key={c.ID} value={c.ID}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <label style={{ fontWeight: 400, fontSize: 12, margin: '0 0 6px' }}>
        <input type="checkbox" checked={safe} onChange={(e) => setSafe(e.target.checked)} /> incident-safe
      </label>
      <button type="button" className="btn btn-primary btn-sm" onClick={add}>
        Add{page.scope === 'company' ? ' for ' + page.companyName : ' to Global'}
      </button>
      <NoteText note={note} />
    </div>
  )
}

function VettedRow({ page, row }: { page: AiAgentShell; row: AiVettedRow }) {
  const isCompany = page.scope === 'company'
  const inherited = isCompany && Number(row.company_id ?? 0) === 0
  const mayEdit = !inherited && (page.isOwner || Number(row.company_id ?? 0) === page.companyId)

  const [safe, setSafe] = useState(Number(row.incident_safe) === 1)
  const [person, setPerson] = useState(Number(row.person_scoped ?? 0) === 1)
  const [note, setNote] = useState<Note | null>(null)
  const personCapable = /\{\{(user|username)\}\}/.test(String(row.command))

  const save = () => {
    setNote({ text: '…' })
    void saveAiRunbookSafe({ id: row.ID, incident_safe: safe ? 1 : 0, person_scoped: person && personCapable ? 1 : 0, scope: page.scope })
      .then((r) => setNote(r.ok ? { text: 'Saved' } : { text: r.error ?? 'Failed', bad: true }))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <tr style={inherited ? { color: '#6b7480' } : undefined}>
      <td style={{ textAlign: 'center' }}>
        <input type="checkbox" checked={safe} disabled={!mayEdit} onChange={(e) => setSafe(e.target.checked)} />
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          className="odrb-person"
          aria-label="about the person"
          checked={person && personCapable}
          disabled={!mayEdit || !personCapable}
          title={personCapable ? 'About the person who asks (email requests)' : 'Needs {{user}} or {{username}} in the command'}
          onChange={(e) => setPerson(e.target.checked)}
        />
      </td>
      <td>
        <strong>{row.command_title}</strong> <span className="text-muted">#{row.ID}</span>
      </td>
      <td>
        <code style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{String(row.command).substring(0, 300)}</code>
      </td>
      <td className="text-muted">
        {row.type_name ?? ''} / {row.category_name ?? ''}
      </td>
      <td>
        {Number(row.company_id ?? 0) === 0 ? (
          isCompany ? (
            <em>inherited from Global</em>
          ) : (
            'Global'
          )
        ) : (
          'this company'
        )}
      </td>
      <td>
        {mayEdit ? (
          <button type="button" className="btn btn-default btn-xs" onClick={save}>
            Save
          </button>
        ) : null}
        <div style={{ fontSize: 11 }}>
          <NoteText note={note} />
        </div>
      </td>
    </tr>
  )
}
